import { setMovieData, setNewMovieBackground } from '../slices/addMediaSlice';
import { RootState } from '../store';
import { buildMovieData } from './buildMovieData';
import { buildCatalogQuery, catalogCacheKey } from './catalogQuery';
import { mediaManagerServerApi } from './mediaManagerServerApi';
import { pollIngestionJobUntilDone } from './pollIngestionJob';
// import { navigationSlice } from '../slices/navigationSlice';
// import { setMoviePageStatus } from '../slices/navigationSlice';
// import { useAppDispatch } from '../hooks';

// const dispatch = useAppDispatch();

/**
 * What a subtitle check puts in its body.
 *
 * Named and exported so it can be tested on its own. It was written
 * inline as `{ name, content }`, and when the panel started sending the
 * row's language that field was simply dropped - a body built by naming
 * fields keeps only the fields it names. Everything either side looked
 * right, so nothing failed: the warning for a subtitle in the wrong row
 * was sent and never arrived.
 */
/** Everything the check is told about the file being checked. */
export type CheckSubtitleData = {
  name: string;
  content: string;
  /**
   * Optional to pass, never optional to name. A row may genuinely have
   * no language chosen yet, and the backend treats absent and undefined
   * alike - but the key is written out below either way, because the
   * whole failure being guarded against here is a field quietly not
   * being in the body.
   */
  language?: string | null;
};

/**
 * Written out field by field against the type rather than spread, so
 * that adding one to CheckSubtitleData and forgetting it here does not
 * compile. This is the body the row's language fell out of: it was
 * destructured from an untyped argument, the caller stopped passing
 * language, and the only sign was a warning that never appeared.
 */
export const checkSubtitleBody = (data: CheckSubtitleData) => {
  const body: {
    [K in keyof Required<CheckSubtitleData>]: CheckSubtitleData[K];
  } = {
    name: data.name,
    content: data.content,
    language: data.language,
  };
  return body;
};

/**
 * What the chapters step puts on the wire.
 *
 * creditsAt is the whole point of the step when no file was uploaded,
 * and it was the one field never appended here. The step set it on its
 * form data, this built the body from a list that did not name it, and
 * it went no further - so the backend saw no time to build chapters
 * from and looked instead for a file nobody had picked. That raised,
 * and what came back to the wizard was an HTML error page it could only
 * report as "Unexpected token '<'".
 *
 * Named and exported so it can be tested. The body being built by
 * naming fields is the second time a field has gone missing that way;
 * naming them somewhere a test can see is the answer to both.
 */
/** Everything the chapters step can put on the wire. */
export type ChaptersStepData = {
  hasNamedChapters?: boolean;
  chaptersFile?: Blob;
  creditsAt?: number | null;
  chaptersInterval?: number | null;
  chapters?: string;
};

/**
 * How each of those becomes a form field, one entry per field.
 *
 * The mapped type is the whole point. Leave a field of ChaptersStepData
 * out of this object and the file does not compile - which is the only
 * mechanism here that would have caught any of the three fields that
 * went missing on the wire. Each of them was set by a step, never named
 * by the hand-written body beside it, and simply absent from the
 * request: no type error, because the input was any; no runtime error,
 * because an object is not required to have a key; no failing test,
 * because nobody writes a test for a field they have forgotten.
 *
 * Returning undefined means "do not send this one". That matters for
 * more than tidiness - FormData turns an absent value into the literal
 * string "undefined", which the backend reads as an answer.
 */
const CHAPTERS_FIELDS: {
  [K in keyof Required<ChaptersStepData>]: (
    value: ChaptersStepData[K],
  ) => string | Blob | undefined;
} = {
  // A boolean, not whatever the form is holding: a checkbox nobody
  // touched is undefined, and that is not an answer either. Always
  // sent, which is why this one never returns undefined.
  hasNamedChapters: (value) => (value ? 'true' : 'false'),
  // Only when there is one. With the credits found instead, or the
  // film's own marks taken, there is no file at all.
  chaptersFile: (value) => value || undefined,
  creditsAt: (value) =>
    value === undefined || value === null ? undefined : String(value),
  // Only alongside a credits time - it is the stride the marks either
  // side of it are made on, and means nothing without one.
  chaptersInterval: (value) =>
    value === undefined || value === null ? undefined : String(value),
  chapters: (value) => value || undefined,
};

export const chaptersStepFields = (data: ChaptersStepData) => {
  const fields: Record<string, string | Blob> = {};
  (Object.keys(CHAPTERS_FIELDS) as (keyof ChaptersStepData)[]).forEach(
    (name) => {
      const take = CHAPTERS_FIELDS[name] as (
        value: unknown,
      ) => string | Blob | undefined;
      const value = take(data[name]);
      if (value !== undefined) {
        fields[name] = value;
      }
    },
  );
  return fields;
};

export const extendedApiSlice = mediaManagerServerApi.injectEndpoints({
  endpoints: (builder) => ({
    grabMovieFrame: builder.mutation<any, any>({
      // The frame at one second of the movie's own file, to be its
      // background. Queued, because only the machine holding the library
      // can open the file - the picture comes back in the finished
      // job rather than being stored anywhere, so a frame looked at
      // and turned down leaves nothing behind.
      query: ({
        id,
        seconds,
        crop = true,
      }: {
        id: number;
        seconds: number;
        crop?: boolean;
      }) => ({
        url: `movie/movies/${id}/grab_frame/`,
        method: 'POST',
        body: { seconds, crop },
      }),
    }),

    getMovieVideoUrl: builder.query<any, any>({
      // Where the movie can be played from, signed. Needed before the
      // step that writes down where the file went, so the address is
      // worked out from where the file has to be.
      query: (id: number) => `movie/movies/${id}/video_url/`,
    }),

    movieFrameAt: builder.mutation<any, any>({
      // The film at one second, at the size it really is. Only the
      // machine holding the library can look, so this queues a job.
      //
      // Asked for rather than offered: the filmstrip is degraded on
      // purpose so that scrubbing the end of a film nobody has seen
      // does not show them how it ends. This is the deliberate way
      // past that, for one second somebody already chose.
      query: ({ id, at }) => ({
        url: `movie/movies/${id}/frame_at/`,
        method: 'POST',
        body: { at },
      }),
    }),

    chaptersInMovieFile: builder.mutation<any, any>({
      // What chapters the film already carries. Only the machine
      // holding the library can look, so this queues a job to poll.
      //
      // Asked before the other two ways are offered, because a release
      // that brought its own is offering better marks than either: it
      // was somebody setting them at the cuts, having watched it.
      query: (id) => ({
        url: `movie/movies/${id}/chapters_in_file/`,
        method: 'POST',
        body: {},
      }),
    }),

    detectMovieCredits: builder.mutation<any, any>({
      // Where the credits begin, so a film that came without chapters
      // can have them made without anyone opening the end of it and
      // spoiling it for themselves. Only the machine holding the
      // library can read the file, so this queues a job to poll.
      //
      // A proposal, never a decision: what comes back is shown, and the
      // chapters are only made once it has been agreed to.
      query: (id: number) => ({
        url: `movie/movies/${id}/detect_credits/`,
        method: 'POST',
      }),
    }),

    probeMovieAudio: builder.mutation<any, any>({
      // Only the machine holding the library can look at the file, so
      // this queues a job and comes back with its id to poll.
      query: (id: number) => ({
        url: `movie/movies/${id}/probe_audio/`,
        method: 'POST',
      }),
    }),

    previewMovieSubtitle: builder.mutation<any, any>({
      // The film's address and the subtitle as WebVTT, to watch
      // the two together before either is committed to. Answers
      // at once rather than queueing: nothing here reads the
      // film, it only says where it is.
      //
      // The file goes up as bytes like the check's does, and
      // comes back converted, because a browser left to decide
      // what a subtitle is encoded in is how accents turn into
      // symbols - and a preview showing those would have somebody
      // fixing a file that was never broken.
      query: ({ id, content }) => ({
        url: `movie/movies/${id}/preview_subtitle/`,
        method: 'POST',
        body: { content },
      }),
    }),

    checkMovieSubtitle: builder.mutation<any, any>({
      // Whether a subtitle a person has just picked belongs to this
      // film, and what is in it that nobody wants on screen. Only the
      // machine holding the library can answer - it is read off the
      // film's own sound - so this queues a job to poll.
      //
      // The file goes up with the request and is not stored. At this
      // point in the wizard nothing has been committed to, and one that
      // turns out to be the wrong film should leave nothing behind.
      //
      // content is base64 of the file's bytes, not its text. A
      // downloaded subtitle can be in any encoding and the file does
      // not say which, so letting the browser decode it is how accents
      // become symbols; the backend works it out. See
      // components/CheckSubtitle/bytes.
      query: ({ id, ...sent }) => ({
        url: `movie/movies/${id}/check_subtitle/`,
        method: 'POST',
        body: checkSubtitleBody(sent),
      }),
    }),

    fixMovieSubtitle: builder.mutation<any, any>({
      // Carrying out what the check proposed. This one answers at once
      // rather than queueing: a shift, a stretch and a tidy are done to
      // the text and need neither the film nor its sound.
      //
      // Not a detail route - there is no film involved, only the file.
      // What comes back is the corrected file for the wizard to hold
      // until it is submitted, so it is saved and converted to WebVTT
      // once, like any other.
      query: (body) => ({
        url: 'movie/movies/fix_subtitle/',
        method: 'POST',
        body,
      }),
    }),

    muxMovie: builder.mutation<any, any>({
      // audioLanguages names each sound track's language in order, and
      // is only needed when the file has more than one: which track is
      // which cannot be worked out, only chosen.
      // dependsOn names a job that has to succeed first - the wizard
      // passes the finalize it has just queued, so the two go in
      // together and the file is written as soon as it has been read.
      query: ({
        id,
        audioLanguages,
        mediaLanguage,
        dependsOn,
        removeAudio,
        mainAudio,
      }: any) => ({
        url: `movie/movies/${id}/mux/`,
        method: 'POST',
        body: {
          audio_languages: audioLanguages,
          media_language: mediaLanguage,
          depends_on: dependsOn,
          remove_audio: removeAudio,
          main_audio: mainAudio,
        },
      }),
      invalidatesTags: ['FinishingMovie'],
    }),

    getMovies: builder.query<any, any | void>({
      // See buildCatalogQuery: every value is encoded, and a filter
      // holding several values becomes a repeated parameter.
      query: (object) => `movie/movies/?${buildCatalogQuery(object)}`,

      transformResponse: (rawResult: {
        results: any;
        currentPage: number;
        totalPages: number;
      }) => {
        // // mediaManagerServerApi.
        // console.log('RAW RESPONSE:', rawResult);
        // // console.log("CURRENT PAGE:", rawResult.currentPage)
        // // console.log("TOTAL PAGES:", rawResult.totalPages)
        // // dispatch(setMoviePageStatus({currentPage: rawResult.currentPage, totalPages: rawResult.totalPages}))
        // return rawResult.results;
        // console.log('I ENTERED THE TRANSFORM');
        return {
          results: rawResult.results,
          currentPage: rawResult.currentPage,
          totalPages: rawResult.totalPages,
        };
      },

      // providesTags: (result = {}) => [
      //   'Movie',
      //   ...result?.results?.map(({ id }: { id: number }) => ({
      //     type: 'Movie',
      //     id,
      //   })),
      // ],

      // providesTags: (result) =>
      //   result
      //     ? [
      //         ...result.results.map(({ id }: { id: number }) => ({ type: 'Movies' as const, id })),
      //         { type: 'Movies', id: 'LIST' },
      //       ]
      //     : [{ type: 'Movies', id: 'LIST' }],

      // providesTags: (result, error, arg) =>
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({ id }: { id: number }) => ({
                type: 'Movie' as const,
                id,
              })),
              'Movie',
            ]
          : ['Movie'],

      // serializeQueryArgs: ({ endpointName }) => {
      //   return endpointName
      // },
      // Keyed on everything but the page, so all pages of one filter
      // set share an entry and merge below can append them. See
      // catalogQueryCacheKey for why the values are sorted and the empty
      // ones dropped - without that, the same filters chosen in a
      // different order would be a different entry and get refetched.
      // The key carries the endpoint name because the cache is one flat
      // map; without it the three catalogs share entries.
      serializeQueryArgs: catalogCacheKey,
      // Always merge incoming data to the cache entry
      merge: (currentCache, newItems) => {
        // console.log('currentCache:', currentCache);
        // console.log('newItems:', newItems);

        //#1
        currentCache.results.push(...newItems.results);
        currentCache.currentPage = newItems.currentPage;
        //#2
        // if (newItems.currentPage > currentCache.currentPage) {
        //   currentCache.results.push(...newItems.results);
        //   currentCache.currentPage = newItems.currentPage;
        // } else {
        //   currentCache.results = newItems.results;
        //   currentCache.currentPage = newItems.currentPage;
        // }
      },
      // Refetch when the page arg changes
      forceRefetch({ currentArg, previousArg }) {
        // console.log('currentArg:', currentArg);
        // console.log('previousArg:', previousArg);
        // return currentArg !== previousArg
        // console.log('CURRENT ARG PAGE:', currentArg?.page);
        // console.log('PREVIOUS ARG PAGE:', previousArg?.page);
        // console.log(
        //   'PAGES DIFF RESULT:',
        //   currentArg?.page > previousArg?.page,
        // );
        // return currentArg?.page !== previousArg?.page;
        // return currentArg !== previousArg;
        //#1
        return currentArg?.page > previousArg?.page;
        //#2
        // return currentArg?.page !== previousArg?.page;
      },
    }),
    getMovie: builder.query<any, string | undefined>({
      query: (id) => `movie/movies/${id}/`,
      // providesTags: ['Movie'],
      providesTags: (result, error, arg) => [{ type: 'Movie', id: arg }],
    }),
    getRandomMovie: builder.query<any, any | void>({
      query: (object) =>
        `movie/movies/get_random_movie/?mode=${object?.mode || ''}`,
      // providesTags: ['Movie'],
      providesTags: (result, error, arg) => [{ type: 'Movie', id: arg }],
    }),

    getSimilarMovies: builder.query<any, any | void>({
      query: (object) =>
        `movie/movies/get_similar_movies/?mode=${object?.mode || ''}&id=${object?.id || ''}`,
      // providesTags: ['Movie'],
      providesTags: (result, error, arg) => [{ type: 'Movie', id: arg }],
    }),

    getItunesAndImdbMovieOptions: builder.query<any, any | void>({
      query: (object) =>
        `movie/movies/get_itunes_and_imdb_movie_options/?movie_title=${encodeURIComponent(object.movieTitle)}&itunes_country=${object?.itunesCountry ? object.itunesCountry.value : null}`,
      transformResponse: (rawResult: {
        itunesSearchMovieResults: any;
        imdbSearchMovieResults: any;
      }) => {
        const processedItunesSearchResults =
          rawResult.itunesSearchMovieResults.map((searchResult: any) => {
            return {
              title: searchResult.trackName,
              cover: searchResult.artworkUrl100.replace(
                '100x100bb',
                '100000x100000-999',
              ),
              id: searchResult.trackId,
            };
          });
        // console.log(
        //   'processedItunesSearchResults:',
        //   processedItunesSearchResults,
        // );
        const processedImdbSearchResults =
          rawResult.imdbSearchMovieResults.map((searchResult: any) => {
            return {
              title: searchResult.Title,
              cover: searchResult.Poster,
              id: searchResult.imdbID,
            };
          });
        return {
          itunesSearchMovieResults: processedItunesSearchResults,
          imdbSearchMovieResults: processedImdbSearchResults,
        };
      },
    }),

    // getMediaData: builder.query<any, any | void>({
    //   query: (object) =>
    //     `movie/movies/get_media_data/?itunes_movie_id=${object.selectedItunesResult}&imdb_movie_id=${object.selectedImdbResult}&country=${object.selectedCountry}`,
    //   // transformResponse: (rawResult: {
    //   //   itunesSearchMovieResults: any;
    //   //   imdbSearchMovieResults: any;
    //   // }) => {
    //   //   const processedItunesSearchResults = rawResult.itunesSearchMovieResults.map((searchResult: any) => {
    //   //     return {
    //   //       title: searchResult.trackName,
    //   //       cover: searchResult.artworkUrl100,
    //   //       id: searchResult.trackId,
    //   //     }
    //   //   })
    //   //   console.log("processedItunesSearchResults:", processedItunesSearchResults)
    //   //   const processedImdbSearchResults = rawResult.imdbSearchMovieResults.map((searchResult: any) => {
    //   //     return {
    //   //       title: searchResult.Title,
    //   //       cover: searchResult.Poster,
    //   //       id: searchResult.imdbID,
    //   //     }
    //   //   })
    //   //   return {
    //   //     itunesSearchMovieResults: processedItunesSearchResults,
    //   //     imdbSearchMovieResults: processedImdbSearchResults,
    //   //   };
    //   // },
    // }),

    getMediaData: builder.query<any, any | void>({
      queryFn: async (object, api, _extraOptions, baseQuery) => {
        // const state = api.getState() as RootState;
        try {
          const result: any = await baseQuery({
            url: `movie/movies/get_media_data/?itunes_movie_id=${object.selectedItunesResult}&imdb_movie_id=${object.selectedImdbResult}&itunes_country=${object.selectedItunesCountry}`,
          });
          if (result?.error) {
            throw result.error;
          }
          const initialData = result.data;

          // Backgrounded server-side (see core/ingestion_jobs.py) -
          // the OMDB lookup + IMDb page scrapes + cast/crew scrape
          // this kicks off can run several minutes for a large cast,
          // which used to exceed Azure App Service's hard ~240s
          // platform timeout and silently kill the request. The
          // initial response above now only carries a job id plus
          // the cheap dropdown-option siblings; poll until the
          // actual scrape result is ready. Encapsulated entirely
          // here so callers (MovieStep4.tsx and friends) see no
          // difference - they still just await a query that
          // eventually resolves or rejects.
          const finishedJob = await pollIngestionJobUntilDone(
            api.dispatch,
            extendedApiSlice.endpoints.getIngestionJob,
            initialData.job.id,
          );
          const scrapedMovieData = finishedJob.resultData;

          // Shared with the queued flow (see continueQueuedMovie),
          // which produces the identical payload and has to land in
          // redux identically - otherwise every step after this one
          // would need to know which flow it came from.
          api.dispatch(
            setMovieData(buildMovieData(scrapedMovieData, initialData)),
          );

          // "genres": new_genres,
          // "languages": new_languages,
          // "countries": new_countries,
          // "tags": [{}],
          // "collections": [{}],
          // # "runtime": 0,
          // "cast": cast,
          // "directors": directors,
          // "screenwriters": screenwriters,
          // "producers": producers,
          // "editing_completed": False,

          // const usageParameters = state.config.usageParameters;
          // const { currency, amount, concept, externalId, bankCodes } = data;
          // const newUsageParameters = {
          //   ...usageParameters,
          //   currency,
          //   amount,
          //   concept,
          //   externalId,
          //   bankCodes,
          // } as IUsageParameters;
          // api.dispatch(setUsageParameters(newUsageParameters));
          // const countries = state.config.countries;
          // if (countries.length === 1) {
          //   const filteredBanks = filterBanks(countries[0], bankCodes);
          //   if (!filteredBanks) {
          //     throw { status: 'invalidBanks', error: 'Invalid banks' };
          //   } else {
          //     const newCountryData = { ...countries[0] };
          //     newCountryData.banks = filteredBanks;
          //     api.dispatch(setCountries([newCountryData]));
          //   }
          // }
          // Preserves the same overall shape callers previously got
          // back (movieData populated, siblings alongside it),
          // even though it now arrives via the finished job's
          // resultData instead of directly in the initial response.
          return { data: { ...initialData, movieData: scrapedMovieData } };
        } catch (errorData: any) {
          // `onError` side-effect
          // console.log('ERROR ENTERED');
          return { error: errorData };
        }
      },
    }),

    createMovie: builder.mutation({
      query: (data) => ({
        url: `movie/movies/`,
        method: 'POST',
        body: data,
        // headers: {
        //   'Content-Type': 'multipart/form-data;'
        // },
        // formData: true,
      }),
    }),

    // patchMovie: builder.mutation({
    //   query: ({ id, ...data }) => ({
    //     url: `movie/movies/${id}/`,
    //     method: 'PATCH',
    //     body: data,
    //     // headers: {
    //     //   'Content-Type': 'multipart/form-data;'
    //     // },
    //     // formData: true,
    //   }),
    // }),

    patchMovie: builder.mutation<any | any, any>({
      queryFn: async (data, api, _extraOptions, baseQuery) => {
        const state = api.getState() as RootState;
        const newMovieId = state.addMedia.newMovieId;
        const currentMovieStep = state.addMedia.currentMovieStep;
        let formData = new FormData();
        // console.log('PRE DATA:', data);
        formData.append('step', data.step);
        if (currentMovieStep === 7) {
          formData.append('cover', data.cover);
          formData.append('background', data.background);
        } else if (currentMovieStep === 8) {
          formData = data;
        } else if (currentMovieStep === 9) {
          // formData.append('languages', data.languages)

          for (let i = 0; i < data.languages.length; i++) {
            formData.append('subtitlesSrt', data.languages[i]?.subtitleSrt);
          }

          // for (let i = 0; i < data.languages.length; i++) {
          //   formData.append('subtitlesVtt', data.languages[i]?.subtitleVtt);
          // }

          // formData.delete("languages")
          for (let i = 0; i < data.languages.length; i++) {
            data.languages[i].subtitleSrt =
              data.languages[i]?.subtitleSrt?.name;
            // data.languages[i].subtitleVtt =
            //   data.languages[i]?.subtitleVtt?.name;
          }
          formData.append('languages', JSON.stringify(data.languages));
        } else if (currentMovieStep === 10) {
          Object.entries(chaptersStepFields(data)).forEach(([name, value]) =>
            formData.append(name, value as any),
          );
        } else if (currentMovieStep === 11) {
          formData = data;
        }

        try {
          const result: any = await baseQuery({
            url: `movie/movies/${newMovieId}/`,
            method: 'PATCH',
            body: formData,
          });
          if (result?.error) {
            throw result.error;
          }
          const data = result.data;
          return { data: data };
        } catch (errorData: any) {
          // `onError` side-effect
          return { error: errorData };
        }
      },
    }),

    getMovieOptions: builder.query<any, any | void>({
      query: (object) =>
        `movie/movies/?keyword=${encodeURIComponent(object.movieTitle)}&mode=add_autograph`,
      transformResponse: (rawResult: { results: any }) => {
        const processedMovieSearchResults = rawResult.results.map(
          (searchResult: any) => {
            return {
              title: searchResult.title,
              cover: searchResult.cover,
              id: searchResult.id,
              imdbId: searchResult.imdbId,
            };
          },
        );
        // console.log(
        //   'processedMovieSearchResults:',
        //   processedMovieSearchResults,
        // );
        return {
          movieSearchResults: processedMovieSearchResults,
        };
      },
    }),

    // createMovie: builder.mutation<any | any, any>({
    //   queryFn: async (data, api, _extraOptions, baseQuery) => {
    //     const state = api.getState() as RootState;

    //     const { generalMovieData, secondaryMovieData, castCrewMovieData, imagesMovieData, languagesMovieData, chaptersMovieData } = state.addMedia;
    //     // const fullMovieData = { ...generalMovieData, ...secondaryMovieData, ...castCrewMovieData, ...imagesMovieData, ...languagesMovieData, ...chaptersMovieData, ...data, mode: "new"}

    //     const fullMovieData = { ...generalMovieData, ...secondaryMovieData, ...data, mode: "new"}

    //     console.log("FULL MOVIE DATA:", fullMovieData)
    //     const formData = new FormData()
    //     // formData.append('background', data.background)
    //     // formData.append('title', fullMovieData.title)
    //     // fileFormData.append("background", fullMovieData.background);

    //     for (const [key, value] of Object.entries(fullMovieData)) {
    //       // fileFormData.append(key, JSON.stringify(value));
    //       formData.append(key, JSON.stringify(value));
    //     }

    //     formData.delete("chaptersFile")
    //     formData.append("chaptersFile", fullMovieData.chaptersFile);

    //     formData.delete("poster")
    //     formData.append("poster", fullMovieData);

    //     formData.delete("background")
    //     formData.append("background", fullMovieData.background);

    //     for (let i = 0 ; i < fullMovieData.languages.length ; i++) {
    //       formData.append("subtitlesSrt", fullMovieData.languages[i]?.subtitleSrt);
    //     }

    //     for (let i = 0 ; i < fullMovieData.languages.length ; i++) {
    //       formData.append("subtitlesVtt", fullMovieData.languages[i]?.subtitleVtt);
    //     }

    //     formData.delete("languages")
    //     for (let i = 0 ; i < fullMovieData.languages.length ; i++) {
    //       fullMovieData.languages[i].subtitleFileSrt = fullMovieData.languages[i]?.subtitleFileSrt?.name
    //       fullMovieData.languages[i].subtitleFileVtt = fullMovieData.languages[i]?.subtitleFileVtt?.name
    //     }
    //     formData.append("languages", JSON.stringify(fullMovieData.languages))

    //     // fileFormData.append("background", fullMovieData.background);

    //     console.log("FILE FORM DATA:", formData)

    //     try {
    //       const result: any = await baseQuery({
    //         url: `movie/movies/`,
    //         method: 'POST',
    //         body: formData,
    //         // data: fileFormData,
    //     //     formData: true,
    //     //             headers: {
    //     //   'Content-Type': 'multipart/form-data;'
    //     // },
    //       });
    //       if (result?.error) {
    //         throw result.error;
    //       }
    //       const data = result.data;
    //       return { data: data };
    //     } catch (errorData: any) {
    //       // `onError` side-effect
    //       return { error: errorData };
    //     }
    //   },
    // }),

    getMovieUser: builder.query<any, string | undefined>({
      query: (id) => `movie/movie_users/${id}/`,
      // providesTags: ['Movie'],
      providesTags: (result, error, arg) => [{ type: 'MovieUser', id: arg }],
    }),

    saveMovieUser: builder.mutation({
      query: (data) => ({
        url: `movie/movie_users/`,
        method: 'POST',
        body: data,
        // headers: {
        //   'Content-Type': 'multipart/form-data;'
        // },
        // formData: true,
      }),
      // invalidatesTags: (result, error, arg) => [{ type: 'MovieUser' }],
      invalidatesTags: () => [{ type: 'MovieUser' }],
    }),

    getMovieBackground: builder.query<any, void>({
      queryFn: async (object, api, _extraOptions, baseQuery) => {
        const state = api.getState() as RootState;
        const newMovieId = state.addMedia.newMovieId;
        // const newMovieId = 100;
        try {
          const result: any = await baseQuery({
            url: `movie/movies/get_movie_background/?movie_id=${newMovieId}`,
          });
          if (result?.error) {
            throw result.error;
          }
          const data = result.data;

          api.dispatch(setNewMovieBackground(data));

          return { data: data };
        } catch (errorData: any) {
          // `onError` side-effect
          // console.log('ERROR ENTERED');
          return { error: errorData };
        }
      },
    }),

    // Movie creation is backgrounded server-side (it scrapes IMDB
    // for every cast/crew member's photo, which can take minutes) -
    // createMovie returns a job immediately, and the caller polls
    // this to find out when it's actually done.
    getIngestionJob: builder.query<any, string>({
      query: (jobId) => `core/ingestion-jobs/${jobId}/`,
    }),
    retryIngestionJob: builder.mutation<any, string>({
      query: (jobId) => ({
        url: `core/ingestion-jobs/${jobId}/retry/`,
        method: 'POST',
      }),
      invalidatesTags: [
        'QueuedMovie',
        'QueuedEpisode',
        'FinishingMovie',
        'FinishingEpisode',
      ],
    }),

    // The queued alternative to getMediaData: start the same scrape,
    // but hand back the job straight away instead of waiting on it.
    // The filters panel's lists, in one request. Mapped to what
    // react-select wants here rather than in the component, so every
    // consumer gets the same shape - the API returns {id, label} and
    // the catalog filters by label, which is the name the backend
    // matches on.
    getMovieCatalogFilterOptions: builder.query<any, void>({
      query: () => `movie/movies/catalog_filter_options/`,
      transformResponse: (raw: any) => {
        const toOptions = (items: any[] = []) =>
          items.map((item: any) => ({
            value: item.label,
            label: item.label,
          }));
        return {
          genres: toOptions(raw?.genres),
          languages: toOptions(raw?.languages),
          countries: toOptions(raw?.countries),
          tags: toOptions(raw?.tags),
          collections: toOptions(raw?.collections),
        };
      },
    }),

    queueMovieMediaData: builder.mutation<any, any>({
      query: (object) => ({
        url: `movie/movies/queue_media_data/`,
        method: 'POST',
        body: {
          itunesMovieId: object.selectedItunesResult,
          imdbMovieId: object.selectedImdbResult,
          itunesCountry: object.selectedItunesCountry,
          title: object.title,
        },
      }),
      invalidatesTags: ['QueuedMovie'],
    }),

    getQueuedMovies: builder.query<any, any | void>({
      query: (object) =>
        `core/ingestion-jobs/queued/?model_name=movie&page=${object?.page || 1}`,
      providesTags: ['QueuedMovie'],
    }),

    // The films whose file is being written. A separate list from the
    // queued scrapes above because the two are different stages -
    // that one is work waiting for a person, this one is work already
    // handed to the worker and running without anybody watching.
    getFinishingMovies: builder.query<any, any | void>({
      query: (object) =>
        `core/ingestion-jobs/finishing/?model_name=movie&page=${
          object?.page || 1
        }`,
      providesTags: ['FinishingMovie'],
    }),

    dismissIngestionJob: builder.mutation<any, string>({
      query: (jobId) => ({
        url: `core/ingestion-jobs/${jobId}/dismiss/`,
        method: 'POST',
      }),
      // All four lists. One endpoint serves the queue and finishing
      // screens of both wizards - the episode screens import this very
      // mutation from here - and which list a job was on is not
      // something the caller should have to say.
      invalidatesTags: [
        'QueuedMovie',
        'QueuedEpisode',
        'FinishingMovie',
        'FinishingEpisode',
      ],
    }),

    // Picks a finished queued scrape back up. Does the same job
    // getMediaData does once its polling finishes - drop the payload
    // into redux in the shape the wizard's steps expect - except the
    // scrape already happened, so there is nothing to wait for.
    continueQueuedMovie: builder.mutation<any, string>({
      queryFn: async (jobId, api, _extraOptions, baseQuery) => {
        try {
          const result: any = await baseQuery({
            url: `movie/movies/queued_media_data/?job_id=${jobId}`,
          });
          if (result?.error) {
            throw result.error;
          }
          const data = result.data;
          api.dispatch(setMovieData(buildMovieData(data.movieData, data)));
          return { data };
        } catch (errorData: any) {
          return { error: errorData };
        }
      },
    }),
  }),
});

export const {
  useMovieFrameAtMutation,
  useChaptersInMovieFileMutation,
  useDetectMovieCreditsMutation,
  useProbeMovieAudioMutation,
  useMuxMovieMutation,
  usePreviewMovieSubtitleMutation,
  useCheckMovieSubtitleMutation,
  useFixMovieSubtitleMutation,
  useGetMoviesQuery,
  useGetMovieQuery,
  useGetRandomMovieQuery,
  useGetItunesAndImdbMovieOptionsQuery,
  useGetMediaDataQuery,
  useCreateMovieMutation,
  usePatchMovieMutation,
  useGetMovieOptionsQuery,
  useGetMovieUserQuery,
  useSaveMovieUserMutation,
  useGetSimilarMoviesQuery,
  useGetMovieBackgroundQuery,
  useLazyGetIngestionJobQuery,
  useRetryIngestionJobMutation,
  useQueueMovieMediaDataMutation,
  useGetMovieCatalogFilterOptionsQuery,
  useGetQueuedMoviesQuery,
  useGetFinishingMoviesQuery,
  useDismissIngestionJobMutation,
  useContinueQueuedMovieMutation,
  useGrabMovieFrameMutation,
  useLazyGetMovieVideoUrlQuery,
} = extendedApiSlice;
